import _ from "lodash";
import { useBalanceStore } from "../../_store/balanceStore";
import { useDebtStore } from "../../_store/debtplanStore";
import { useEducationStore } from "../../_store/educationStore";
import { useFamilyStore } from "../../_store/familyStore";
import { useFamilytreeStore } from "../../_store/familytreeStore";
import { useFuturePlanStore } from "../../_store/futurePlanStore";
import { useInsuranceStore } from "../../_store/insuranceStore";
import { useInvestStore } from "../../_store/investStore";
import { useLegacyStore } from "../../_store/legacyStore";
import { useRetireStore } from "../../_store/retireStore";
import { useRiskStore } from "../../_store/riskStore";
import { useSavingStore } from "../../_store/savingStore";
import { useStrategyStore } from "../../_store/strategyRetireStore";
import { useTaxStore } from "../../_store/taxplanStore";
import { useUserStore } from "../../_store/userStore";
import Api from "../../api/api.main";
import { mapFamilyExpensesFromBalance } from "../../functions/insurance/family-expenses";
import { normalizeChildDeductionList } from "../../functions/taxFucntion/child-deduction-state";
import { AverageRetirePerccent } from "../retirement_plan/Retirement_Plan.Main";
import { createProfileSessionLoader } from "./profile-session";

const PLAN_STORES = [
  useSavingStore,
  useRetireStore,
  useTaxStore,
  useRiskStore,
  useBalanceStore,
  useInvestStore,
  useEducationStore,
  useInsuranceStore,
  useFamilyStore,
  useFamilytreeStore,
  useDebtStore,
  useFuturePlanStore,
  useLegacyStore,
  useStrategyStore,
];

// เรียก clear owner ของทุก plan store เพื่อไม่ให้ข้อมูลผู้ใช้ก่อนหน้าปะปนกับ session ใหม่
export const clearPlanState = () => {
  for (const store of PLAN_STORES) {
    const state = store.getState();
    const clear = Object.entries(state).find(
      ([key, value]) => key.startsWith("clear") && typeof value === "function"
    )?.[1];
    clear?.();
  }
};

const getInitialValue = (store, key, fallback) =>
  store.getInitialState?.()?.[key] ?? fallback;

// อ่านค่าเริ่มต้นจาก store owner เพื่อรองรับบัญชีใหม่ที่ยังไม่มี Storage document สมบูรณ์
export const getDefaultPlanState = () => ({
  strategy: getInitialValue(useStrategyStore, "strategy", null),
  retireData: getInitialValue(useRetireStore, "retireData", {}),
  taxData: getInitialValue(useTaxStore, "taxData", {}),
  savingData: getInitialValue(useSavingStore, "savingData", {}),
  balanceData: getInitialValue(useBalanceStore, "balanceData", {}),
  riskData: getInitialValue(useRiskStore, "riskData", {}),
  educationData: getInitialValue(useEducationStore, "educationData", []),
  insuranceData: getInitialValue(useInsuranceStore, "insuranceData", {}),
  familytreeData: getInitialValue(useFamilytreeStore, "familytreeData", []),
  debtData: getInitialValue(useDebtStore, "debtData", {}),
  futurePlanData: getInitialValue(useFuturePlanStore, "futurePlanData", {}),
  investData: getInitialValue(useInvestStore, "investData", {}),
  legacyData: getInitialValue(useLegacyStore, "legacyData", {}),
  familyData: {
    ishasIncome: getInitialValue(useFamilyStore, "ishasIncome", true),
    isMarriage: getInitialValue(useFamilyStore, "isMarriage", true),
    isSpouseDisable: getInitialValue(
      useFamilyStore,
      "isSpouseDisable",
      true
    ),
    ishasChild: false,
    chlidList: [],
  },
});

const mergePlanRoot = (storedValue, defaultValue) => {
  if (Array.isArray(defaultValue)) {
    return Array.isArray(storedValue) ? storedValue : defaultValue;
  }
  if (
    defaultValue &&
    typeof defaultValue === "object" &&
    !Array.isArray(defaultValue)
  ) {
    return { ...defaultValue, ...(storedValue || {}) };
  }
  return storedValue ?? defaultValue;
};

// รองรับอายุ legacy ใน Storage เฉพาะ number หรือ numeric string ที่ไม่ว่าง เป็น finite และมากกว่าศูนย์
const normalizeStoredAge = (age) => {
  if (typeof age === "number") {
    return Number.isFinite(age) && age > 0 ? age : null;
  }
  if (typeof age !== "string" || age.trim() === "") {
    return null;
  }
  const normalizedAge = Number(age);
  return Number.isFinite(normalizedAge) && normalizedAge > 0
    ? normalizedAge
    : null;
};

// รวม Storage กับค่าเริ่มต้นโดยรักษาฟิลด์เดิม แต่ให้อายุจาก Profile ปัจจุบันชนะอายุเก่าใน Storage
// ถ้า Profile ไม่มีอายุจำนวนบวกที่ใช้ได้ จึงค่อย normalize อายุ legacy จาก Storage และ fallback เป็น 25
const mergeStorageWithDefaults = (storage, defaults, profile) => {
  const merged = { ...defaults, ...storage };
  for (const [key, defaultValue] of Object.entries(defaults)) {
    merged[key] = mergePlanRoot(storage?.[key], defaultValue);
  }
  merged.balanceData = merged.balanceData || {};
  merged.insuranceData = merged.insuranceData || {};
  merged.retireData = merged.retireData || {};
  merged.debtData = merged.debtData || {};
  merged.futurePlanData = merged.futurePlanData || {};
  const profileAge = profile?.age;
  const storedAge = normalizeStoredAge(storage?.userData?.age);
  let currentAge = 25;
  if (Number.isFinite(profileAge) && profileAge > 0) {
    currentAge = profileAge;
  } else if (storedAge !== null) {
    currentAge = storedAge;
  }
  merged.userData = {
    ...profile,
    ...(storage?.userData || {}),
    age: currentAge,
  };
  return merged;
};

// คงสูตร normalize เดิมของ Google Login และใช้ค่าอาหารจาก Balance ทับ derived mirror ก่อนกระจายค่าเข้า Zustand stores
export const normalizeProfileStorage = (storage, defaults, profile) => {
  const dataStorage = {
    storage: mergeStorageWithDefaults(storage, defaults, profile),
  };

  // familyData เป็น source of truth ของค่าลดหย่อนบุตร จึง normalize writer ปัจจุบันและข้อมูล legacy ตั้งแต่ขอบ login
  const chlidList = normalizeChildDeductionList(
    dataStorage.storage.familyData?.chlidList
  );
  dataStorage.storage.familyData = {
    ...dataStorage.storage.familyData,
    ishasChild: chlidList.length > 0,
    chlidList,
  };

  const Insurance_key = [
    "expenses_insuarant_home",
    "expenses_insuarant_fire",
    "expenses_insuarant_car",
    "expenses_insuarant_accident",
    "expenses_insuarant_life",
  ];

  let sumInsurancefund = 0;


  let findMinFamilyAge = profile?.family_tree?.map(
    (item, index) => {
      if (item?.relation?.value === "child") {
        return 25 - item?.age;
      }
      return 100 - item?.age;
    }
  );

  // คำนวนทุนประกัน
  dataStorage?.storage?.balanceData?.expenses_fixed_list
    ?.filter((item) =>
      Insurance_key?.includes(item?.options_expenses_fixed)
    )
    ?.forEach((item) => {
      if (item?.options_expenses_fixed === "expenses_insuarant_home") {
        item?.expenses_insuarant_homes?.forEach((item2) => {
          sumInsurancefund += Number(item2?.insurance_fund || 0);
        });
      }

      if (item?.options_expenses_fixed === "expenses_insuarant_fire") {
        item?.expenses_insuarant_fires?.forEach((item2) => {
          sumInsurancefund += Number(item2?.insurance_fund || 0);
        });
      }

      if (item?.options_expenses_fixed === "expenses_insuarant_car") {
        item?.expenses_insuarant_cars?.forEach((item2) => {
          sumInsurancefund += Number(item2?.insurance_fund || 0);
        });
      }

      if (
        item?.options_expenses_fixed === "expenses_insuarant_accident"
      ) {
        item?.expenses_insuarant_accidents?.forEach((item2) => {
          sumInsurancefund += Number(item2?.insurance_fund || 0);
        });
      }

      if (item?.options_expenses_fixed === "expenses_insuarant_life") {
        item?.expenses_insuarant_lifes?.forEach((item2) => {
          sumInsurancefund += Number(item2?.insurance_fund || 0);
        });
      }
    });

  // สินทรัพย์รวม (ยกเว้นสินทรัพย์ส่วนตัว เช่น บ้านที่อยู่อาศัย รถยนต์ที่ใช้ส่วนตัว)

  if (!dataStorage.storage) {
    dataStorage.storage = {};
  }

  if (!dataStorage.storage.insuranceData) {
    dataStorage.storage.insuranceData = {};
    dataStorage.storage.insuranceData.family_year_survive_max = 100;
    dataStorage.storage.insuranceData.family_income_rate_max = 100;
    dataStorage.storage.insuranceData.family_investment_rate_max = 100;
  }

  // Set disabled_income_salary and disabled_income_salary_per_year
  if (dataStorage?.storage?.balanceData) {
    dataStorage.storage.insuranceData.income_salary =
      (dataStorage.storage.balanceData.sum_salary_peryear || 0) / 12;
    dataStorage.storage.insuranceData.income_salary_per_year =
      dataStorage.storage.balanceData.sum_salary_peryear || 0;

    dataStorage.storage.insuranceData.disabled_income_salary =
      (dataStorage.storage.balanceData.sum_salary_peryear || 0) / 12;
    dataStorage.storage.insuranceData.disabled_income_salary_per_year =
      dataStorage.storage.balanceData.sum_salary_peryear || 0;
  }

  if (
    dataStorage?.storage?.balanceData?.property_asset_list?.length >
      0 ||
    dataStorage?.storage?.balanceData?.property_intangible_list
      ?.length > 0 ||
    dataStorage?.storage?.balanceData?.property_invest_list?.length > 0
  ) {
    dataStorage.storage.insuranceData.asset_all = 0;
  }

  if (
    dataStorage?.storage?.balanceData?.property_asset_list?.length > 0
  ) {
    dataStorage.storage.insuranceData.asset_all +=
      dataStorage?.storage?.balanceData?.sum_property_asset;
  }

  if (
    dataStorage?.storage?.balanceData?.property_intangible_list
      ?.length > 0
  ) {
    dataStorage.storage.insuranceData.asset_all +=
      dataStorage?.storage?.balanceData?.sum_property_intangible;
  }

  if (
    dataStorage?.storage?.balanceData?.property_invest_list?.length > 0
  ) {
    dataStorage.storage.insuranceData.asset_all +=
      dataStorage?.storage?.balanceData?.sum_property_invest;
  }

  // หายอดรวมค่าใช้จ่ายหนี้สินรายเดือนทั้งหมด
  const sumExpensesDebtPerMonth = () => {
    let result = 0;
    if (dataStorage?.storage?.balanceData?.sum_debt_short_permonth) {
      result += dataStorage?.storage?.sum_debt_short_permonth;
    }
    if (dataStorage?.storage?.sum_debt_long_permonth) {
      result += dataStorage?.storage?.sum_debt_long_permonth;
    }
    return result;
  };

  // หายอดรวมค่าใช้จ่ายหนี้สินรายปีทั้งหมด
  const sumExpensesDebtPerYear = () => {
    let result = 0;
    result = sumExpensesDebtPerMonth() * 12;
    return result;
  };

  let personal_expenses_saving =
    dataStorage?.storage?.balanceData?.sum_expenses_invest_saving;
  let personal_expenses_life_insurance = 0;
  let personal_expenses_tax = 0;
  let personal_expenses_life_fund = 0;
  let personal_expenses_social_insurance = 0;
  let personal_expenses_consumption = 0;
  const { personal_expenses_food } = mapFamilyExpensesFromBalance(
    dataStorage.storage.insuranceData,
    dataStorage.storage.balanceData
  );
  let personal_expenses_entertainment = 0;
  let personal_expenses_travel = 0;
  let personal_expenses_debt = sumExpensesDebtPerYear();

  if (
    dataStorage?.storage?.balanceData?.expenses_fixed_list?.length > 0
  ) {
    for (const expenses_fixed_item of dataStorage?.storage?.balanceData
      ?.expenses_fixed_list) {
      if (expenses_fixed_item?.expenses_provide_funds?.length > 0) {
        for (const expenses_provide_func_item of expenses_fixed_item.expenses_provide_funds) {
          if (
            expenses_fixed_item.options_expenses_fixed ===
            "expenses_provide_fund"
          ) {
            personal_expenses_life_fund +=
              +expenses_provide_func_item.pay_peryear;
          }
        }
      }
      if (expenses_fixed_item?.expenses_insuarant_socials?.length > 0) {
        for (const expenses_insuarant_socials_item of expenses_fixed_item?.expenses_insuarant_socials) {
          if (
            expenses_fixed_item.options_expenses_fixed ===
            "expenses_insuarant_social"
          ) {
            personal_expenses_social_insurance +=
              +expenses_insuarant_socials_item.pay_peryear;
          }
        }
      }
    }
  }

  if (
    dataStorage?.storage?.balanceData?.expenses_variable_list?.length >
    0
  ) {
    for (const expenses_variable_item of dataStorage?.storage
      ?.balanceData?.expenses_variable_list) {
      if (
        expenses_variable_item?.options_expenses_variable ===
        "expenses_consumer"
      ) {
        personal_expenses_consumption +=
          +expenses_variable_item.pay_peryear;
      }
      if (
        expenses_variable_item?.options_expenses_variable ===
        "expenses_recreation"
      ) {
        personal_expenses_entertainment +=
          +expenses_variable_item.pay_peryear;
      }
    }
  }

  if (
    dataStorage?.storage?.balanceData?.expenses_variable_list?.length >
    0
  ) {
    for (const expenses_variable_item of dataStorage?.storage
      ?.balanceData?.expenses_variable_list) {
      if (
        expenses_variable_item.options_expenses_variable ===
        "expenses_tax"
      ) {
        personal_expenses_tax += +expenses_variable_item.pay_peryear;
      }
    }
  }

  // หาค่าเดินทางทั้งปี
  if (
    dataStorage?.storage?.balanceData?.expenses_variable_list?.length >
    0
  ) {
    for (const expenses_variable_item of dataStorage?.storage
      ?.balanceData?.expenses_variable_list) {
      if (
        expenses_variable_item.options_expenses_variable ===
        "expenses_transport"
      ) {
        personal_expenses_travel += +expenses_variable_item.pay_peryear;
      }
    }
  }

  // หากองทุนสำรองเลี้ยงชีพทั้งปี
  if (
    dataStorage?.storage?.balanceData?.expenses_fixed_list?.length > 0
  ) {
    for (const expenses_fixed_item of dataStorage?.storage?.balanceData
      ?.expenses_fixed_list) {
      if (
        expenses_fixed_item.options_expenses_fixed ===
        "expenses_provide_fund"
      ) {
        for (const item of expenses_fixed_item?.expenses_provide_funds || []) {
          personal_expenses_life_fund += item?.pay_peryear || 0;
        }
      }
    }
  }

  // หาค่าเบี้ยประกันชีวิตทั้งปี
  if (
    dataStorage?.storage?.balanceData?.expenses_fixed_list?.length > 0
  ) {
    for (const expenses_fixed_item of dataStorage?.storage?.balanceData
      ?.expenses_fixed_list) {
      if (
        expenses_fixed_item.options_expenses_fixed ===
        "expenses_insuarant_life"
      ) {
        for (const item of expenses_fixed_item?.expenses_insuarant_lifes || []) {
          personal_expenses_life_insurance += item?.allowance_peryear || 0;
        }
      }
    }
  }

  //======== ภาษี ===============

  dataStorage.storage.insuranceData.tax_income =
    dataStorage?.storage?.balanceData?.sum_salary_peryear || 0;

  //=====================================================================

  // เงินออมทั้งปี(ครอบครัว)
  dataStorage.storage.insuranceData.personal_expenses_saving =
    personal_expenses_saving || 0;

  // เงินออมทั้งปี(ทุพพลภาพ)
  dataStorage.storage.insuranceData.disabled_personal_expenses_saving =
    personal_expenses_saving || 0;

  // ภาษีเงินได้
  dataStorage.storage.insuranceData.personal_expenses_tax =
    personal_expenses_tax || 0;
  // ภาษีเงินได้(ทุพพลภาพ)
  dataStorage.storage.insuranceData.disabled_personal_expenses_tax =
    personal_expenses_tax || 0;

  // กองทุนสำรองเลี้ยงชีพทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_life_fund =
    personal_expenses_life_fund || 0;
  // กองทุนสำรองเลี้ยงชีพทั้งปี(ทุพพลภาพ)
  dataStorage.storage.insuranceData.disabled_personal_expenses_life_fund =
    personal_expenses_life_fund || 0;

  // เงินประกันสังคมทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_social_insurance =
    personal_expenses_social_insurance || 0;
  // เงินประกันสังคมทั้งปี(ทุพพลภาพ)
  dataStorage.storage.insuranceData.disabled_personal_expenses_social_insurance =
    personal_expenses_social_insurance || 0;

  // ค่าใช้จ่ายอุปโภคทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_consumption =
    personal_expenses_consumption || 0;

  // ค่าอาหารทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_food =
    personal_expenses_food || 0;

  // ค่านันทนาการทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_entertainment =
    personal_expenses_entertainment || 0;

  // ค่าเดินทางทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_travel =
    personal_expenses_travel || 0;

  // ค่าเบี้ยประกันชีวิตทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_life_insurance =
    personal_expenses_life_insurance || 0;

  // เงินผ่อนชำระงวดหนี้สินคงค้างทั้งปี
  dataStorage.storage.insuranceData.personal_expenses_debt =
    personal_expenses_debt || 0;

  //====================================================================

  // ทุนประกันชีวิตส่วนตัว

  if (
    dataStorage?.storage?.balanceData?.expenses_fixed_list?.length > 0
  ) {
    for (const expenses_fixed_item of dataStorage?.storage?.balanceData
      ?.expenses_fixed_list) {
      if (expenses_fixed_item?.expenses_insuarant_lifes?.length > 0) {
        for (const expenses_insuarant_item of expenses_fixed_item?.expenses_insuarant_lifes) {
          dataStorage.storage.insuranceData.personal_expenses_life_insurance +=
            expenses_insuarant_item?.allowance_peryear;
          dataStorage.storage.insuranceData.asset_insurance_private +=
            +expenses_insuarant_item?.insurance_fund;
        }
      }
    }
  }

  if (!dataStorage?.storage) {
    dataStorage.storage = {};
  }

  if (!dataStorage?.storage?.insuranceData) {
    dataStorage.storage["insuranceData"] = {};
  }

  if (!dataStorage?.storage?.insuranceData?.asset_is_social_insurance) {
    dataStorage.storage.insuranceData.asset_is_social_insurance = "N";
  }
  dataStorage.storage.insuranceData.asset_is_social_insurance =
    dataStorage?.storage?.insuranceData?.asset_is_social_insurance ||
    "N";

  if (!dataStorage?.storage?.insuranceData?.asset_is_debt) {
    dataStorage.storage.insuranceData.asset_is_debt = "N";
  }
  dataStorage.storage.insuranceData.asset_is_debt =
    dataStorage?.storage?.insuranceData?.asset_is_debt || "N";

  if (
    !dataStorage?.storage?.insuranceData
      ?.disabled_asset_is_social_insurance
  ) {
    dataStorage.storage.insuranceData.disabled_asset_is_social_insurance =
      "N";
  }
  dataStorage.storage.insuranceData.disabled_asset_is_social_insurance =
    dataStorage?.storage?.insuranceData
      ?.disabled_asset_is_social_insurance || "N";

  //====================================================================

  // ทุนประกันทุพลภาพ

  if (
    dataStorage?.storage?.balanceData?.expenses_fixed_list?.length >
      0 &&
    dataStorage?.storage?.balanceData?.expenses_fixed_list
      ?.map((item) => item?.options_expenses_fixed)
      ?.includes("expenses_insuarant_life")
  ) {
    dataStorage.storage.insuranceData.sum_disabled_insurance_current = 0;
    for (const expenses_fixed_item of dataStorage?.storage?.balanceData
      ?.expenses_fixed_list) {
      const keys = Object.keys(expenses_fixed_item);
      for (const key of keys) {
        if (_.isArray(expenses_fixed_item[key])) {
          for (const item_of_key in expenses_fixed_item[key]) {
            // sum_disabled_insurance_current += +item_of_key.insurance_fund;
            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "aia_mutiplay_ci_more",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "aia_total_care_more",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "compensation_aia_ci_plus",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "compensation_aia_ci_plus_gold",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "compensation_aia_ci_topup",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "compensation_aia_supercare",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(item_of_key.more_details, "compensation_ecir", 0);

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "compensation_mutiple_care_ci",
                0
              );

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(item_of_key.more_details, "contract_tpd", 0);

            dataStorage.storage.insuranceData.sum_disabled_insurance_current +=
              +_.get(
                item_of_key.more_details,
                "critical_illness_insurance_yearly_ci",
                0
              );
          }
        }
      }
    }
  }

  //====================================================================

  dataStorage.storage.insuranceData.family_year_survive =  findMinFamilyAge?.length > 0 ? Math.max(
    ...findMinFamilyAge
  ) : 20;

  dataStorage.storage.insuranceData.asset_insurance_private =
    sumInsurancefund;

  if (!dataStorage.storage) {
    dataStorage.storage = {};
  }

  if (!dataStorage.storage.retireData) {
    dataStorage.storage.retireData = {};
  }

  dataStorage.storage.retireData.age_current =
    dataStorage?.storage?.userData?.age || 25;

  dataStorage.storage.retireData.salary =
    dataStorage?.storage?.balanceData?.sum_salary_peryear/12 || 0;

  dataStorage.storage.retireData.other_income =
    dataStorage?.storage?.balanceData?.sum_interest_peryear +
      dataStorage?.storage?.balanceData?.sum_other_income || 0;

  const StrategyLists = dataStorage?.storage?.strategy?.map((item) => {
    return item?.key;
  });

  if (
    StrategyLists?.includes("strategy_investment_bf_retirement_change")
  ) {
    dataStorage.storage.retireData.strategy_investment_bf_retirement_change =
      dataStorage?.storage?.strategy?.find(
        (item) =>
          item?.key === "strategy_investment_bf_retirement_change"
      )?.amount;
  }

  if (
    StrategyLists?.includes("strategy_investment_af_retirement_change")
  ) {
    dataStorage.storage.retireData.strategy_investment_af_retirement_change =
      dataStorage?.storage?.strategy?.find(
        (item) =>
          item?.key === "strategy_investment_af_retirement_change"
      )?.amount;
  }

  dataStorage.storage.retireData.investment_bf_retirement = isNaN(
    AverageRetirePerccent(dataStorage?.storage?.balanceData)
  )
    ? 4
    : AverageRetirePerccent(dataStorage?.storage?.balanceData);

  dataStorage.storage.retireData.investment_af_retirement = isNaN(
    AverageRetirePerccent(dataStorage?.storage?.balanceData) - 2
  )
    ? 2
    : parseFloat(
        AverageRetirePerccent(dataStorage?.storage?.balanceData) - 2
      ).toFixed(1);

  dataStorage.storage.retireData.strategy_investment_bf_retirement_change =
    isNaN(AverageRetirePerccent(dataStorage?.storage?.balanceData))
      ? 4
      : AverageRetirePerccent(dataStorage?.storage?.balanceData);

  dataStorage.storage.retireData.strategy_investment_af_retirement_change =
    isNaN(AverageRetirePerccent(dataStorage?.storage?.balanceData) - 2)
      ? 2
      : parseFloat(
          AverageRetirePerccent(dataStorage?.storage?.balanceData) - 2
        ).toFixed(1);

  if (!dataStorage.storage.debtData) {
    dataStorage.storage.debtData = {};
  }

  dataStorage.storage.debtData.loan_interest_rate = 12;

  if (!dataStorage.storage.futurePlanData) {
    dataStorage.storage.futurePlanData = {};
  }

  if (!dataStorage.storage.futurePlanData.cash_flow_items) {
    dataStorage.storage.futurePlanData.cash_flow_items = [];
  }

  dataStorage.storage.futurePlanData.age_current = dataStorage?.storage?.userData?.age || 25;



  return dataStorage.storage;
};

// กระจาย Storage ที่ normalize แล้วกลับเข้า store owner ตามลำดับเดิมของ Login flow
export const applyStorage = (storage) => {
  useStrategyStore.getState().setStrategy(storage.strategy);
  useRetireStore.getState().setRetireData(storage.retireData);
  useTaxStore.getState().setTaxData(storage.taxData);
  useSavingStore.getState().setSavingeData(storage.savingData);
  useBalanceStore.getState().setBalanceData(storage.balanceData);
  useRiskStore.getState().setRiskData(storage.riskData);
  useEducationStore.getState().setEducationData(storage.educationData);
  useInsuranceStore.getState().setInsuranceData(storage.insuranceData);
  const familyState = useFamilyStore.getState();
  familyState.setIshasIncome(Boolean(storage.familyData.ishasIncome));
  familyState.setIsMarriage(Boolean(storage.familyData.isMarriage));
  familyState.setIsSpouseDisable(Boolean(storage.familyData.isSpouseDisable));
  familyState.setIshasChild(Boolean(storage.familyData.ishasChild));
  familyState.setChildList(storage.familyData.chlidList);
  useFamilytreeStore.getState().setFamilytreeData(storage.familytreeData);
  useDebtStore.getState().setDebtData(storage.debtData);
  useFuturePlanStore.getState().setFuturePlanData(storage.futurePlanData);
  useInvestStore.getState().setInvestData(storage.investData);
  useLegacyStore.getState().setLegacyData(storage.legacyData);
};

// ประกอบ shared session loader กับ API และ Zustand owners ที่แอปใช้งานจริง
export const useProfileSession = () => {
  const setUserData = useUserStore((state) => state.setUserData);
  const loader = createProfileSessionLoader({
    api: Api,
    clearPlanState,
    getDefaultPlanState,
    normalizeStorage: normalizeProfileStorage,
    applyStorage,
    setUserData,
  });

  return { loadProfileSession: loader.load };
};
